import { AnalyticsEvent, CHATBOT_WEBHOOK, PostAnalyticsFN } from '../../common/Analytics';
import type { ErrorReporter } from '../../common/ErrorReporter';
import type { default as DB } from '../../db/DB';
import { ReplicantError } from '../../Errors';
import type { Replicant } from '../../ReplicantConfig';
import SB from '../../SchemaBuilder';
import { groupBy } from '../../utils/ArrayUtils';
import type { APIAccessInterface, PushDocsToIndexFn } from '../ServerReplicant';
import { executeOnWebhook } from './executeOnWebhook';

const webhookCommentEventSchema = SB.object({
    field: SB.tuple(['comments' as const]),
    value: SB.object({
        from: SB.object({ id: SB.string(), username: SB.string() }).additionalProperties(),
        id: SB.string(),
        media: SB.object({ id: SB.string(), media_product_type: SB.string() }).additionalProperties(),
        text: SB.string(),
    }).additionalProperties(),
}).additionalProperties();

const messagingEventBaseSchema = {
    sender: SB.object({ id: SB.string() }).additionalProperties(),
};

const messageEventSchema = SB.object({
    ...messagingEventBaseSchema,
    message: SB.object({ mid: SB.string(), text: SB.string().optional() }).additionalProperties(),
}).additionalProperties();

const reactionEventSchema = SB.object({
    ...messagingEventBaseSchema,
    reaction: SB.object({ mid: SB.string(), reaction: SB.string().optional() }).additionalProperties().optional(),
}).additionalProperties();

const postbackEventSchema = SB.object({
    ...messagingEventBaseSchema,
    postback: SB.object({
        mid: SB.string(),
        referral: SB.object({ ref: SB.string().optional() }).additionalProperties().optional(),
    }).additionalProperties(),
}).additionalProperties();

const referralEventSchema = SB.object({
    ...messagingEventBaseSchema,
    referral: SB.object({ ref: SB.string().optional() }).additionalProperties(),
}).additionalProperties();

const webhookMessagingEventSchema = SB.union([
    messageEventSchema,
    reactionEventSchema,
    postbackEventSchema,
    referralEventSchema,
]);

const webhookRequestBodySchema = SB.object({
    object: SB.tuple(['instagram' as const]),
    entry: SB.array(
        SB.object({
            changes: SB.array(SB.unknown()).optional(),
            messaging: SB.array(SB.unknown()).optional(),
        }).additionalProperties(),
    ),
}).additionalProperties();

export type InstagramWebhookBaseEvent = { platform: 'instagram' };

/** @see https://developers.facebook.com/docs/graph-api/webhooks/reference/instagram/#comments */
export type InstagramWebhookCommentEvent = InstagramWebhookBaseEvent & SB.ExtractType<typeof webhookCommentEventSchema>;

/** @see https://developers.facebook.com/docs/messenger-platform/instagram/features/webhook#messages */
export type InstagramWebhookMessagingEvent = InstagramWebhookBaseEvent &
    SB.ExtractType<typeof webhookMessagingEventSchema>;

export type InstagramWebhookEvent = InstagramWebhookCommentEvent | InstagramWebhookMessagingEvent;

export class InstagramWebhookHandler<T extends Replicant> {
    constructor(
        private opts: {
            apiAccess: APIAccessInterface<T>;
            db: DB<T['state']>;
            errorReporter: ErrorReporter;
            pushDocsToIndex: PushDocsToIndexFn;
            sendAnalyticsEvents: PostAnalyticsFN;
        },
    ) {}

    async handleWebhook(requestBody: unknown): Promise<void> {
        if (!webhookRequestBodySchema.isValid(requestBody)) {
            const error = Error('Unexpected Instagram webhook request body');
            (error as any).requestBody = requestBody;
            throw error;
        }

        const supportedEvents: InstagramWebhookEvent[] = [
            ...requestBody.entry
                .flatMap((entry) => entry.changes ?? [])
                .filter((event): event is SB.ExtractType<typeof webhookCommentEventSchema> =>
                    webhookCommentEventSchema.isValid(event),
                )
                .map((event) => ({ ...event, platform: 'instagram' as const })),

            ...requestBody.entry
                .flatMap((entry) => entry.messaging ?? [])
                .filter((event): event is SB.ExtractType<typeof webhookMessagingEventSchema> =>
                    webhookMessagingEventSchema.isValid(event),
                )
                .map((event) => ({ ...event, platform: 'instagram' as const })),
        ];

        if (supportedEvents.length === 0) {
            return;
        }

        const chatbotWebhookEvents = supportedEvents.map((event) => createChatbotWebhookAnalyticEvent(event));

        await Promise.all([this.opts.sendAnalyticsEvents(chatbotWebhookEvents), this.handleEvents(supportedEvents)]);
    }

    private async handleEvents(events: InstagramWebhookEvent[]): Promise<void> {
        const onWebhook = this.opts.apiAccess.config.chatbot?.events?.onWebhook;

        if (!onWebhook) {
            return;
        }

        const eventsByReceiverId = groupBy(events, getWebhookEventReceiverId);

        const newIndexDocs: { [docId: string]: Record<string, unknown> } = {};

        const receiverPromises = Object.entries(eventsByReceiverId).map(async ([receiverId, receiverEvents]) => {
            try {
                const chatbotMetainfo = await this.opts.db.chatbot.fetchMetainfo(receiverId);

                if (!chatbotMetainfo?.instagramMessengerId) {
                    await this.opts.db.chatbot.setInstagramMessengerId({
                        userId: receiverId,
                        instagramMessengerId: receiverId,
                    });
                }

                const { newIndexDoc } = await executeOnWebhook(
                    receiverId,
                    receiverEvents,
                    this.opts.apiAccess,
                    this.opts.db,
                );

                if (newIndexDoc) {
                    newIndexDocs[receiverId] = newIndexDoc;
                }
            } catch (error: any) {
                const replicantError = new ReplicantError(
                    'IG webhook event handler failed: ' + error.message,
                    'server_error',
                );
                replicantError.extras = { events: receiverEvents, reason: error };
                replicantError.stack = error.stack;

                await this.opts.errorReporter.captureException(replicantError);
            }
        });

        await Promise.all(receiverPromises);

        await this.opts.pushDocsToIndex(newIndexDocs);
    }
}

function createChatbotWebhookAnalyticEvent(event: InstagramWebhookEvent): AnalyticsEvent {
    return {
        user_id: getWebhookEventReceiverId(event),
        event_type: CHATBOT_WEBHOOK,
        event_properties: event as any,
    };
}

function getWebhookEventReceiverId(event: InstagramWebhookEvent): string {
    if (isWebhookCommentEvent(event)) {
        return event.value.from.id;
    } else {
        return event.sender.id;
    }
}

function isWebhookCommentEvent(event: InstagramWebhookEvent): event is InstagramWebhookCommentEvent {
    return event.field === 'comments';
}
